import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Accordion,AccordionSummary,Button,Card,FormControl,InputLabel,MenuItem,Select,Skeleton,Stack } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@tanstack/react-query';
import { ptBR } from 'date-fns/locale';
import { useCallback,useContext,useEffect,useRef,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import { Reportsv2 } from '../../service/Reports';
import { ChecklistReportType, filterDataType, filterObj, filtersV2 } from '../../types/reports/types';
import AutocompleteSemApi from '../Autocomplete/AutoCompeteSemApi';
import { WaringSpan } from '../Users/style/UserRegisterStyle';
import ReportsTableComponent from './ReportsTableComponent';
import { LabelInput,SpanTitle } from './Style';

const currentDate = new Date();
const oneWeekAgo = new Date(currentDate);

const reportsService = new Reportsv2();
type FilterEntity = 'gerencias' | 'usuarios' | 'clientes' | 'locais' | 'conjuntos' | 'unidades' | 'checklists' | 'tiposServicos';
type FilterPage = { options: filterObj[]; loading: boolean; error: boolean; loaded: boolean };

const entityByFilter: Record<FilterEntity, string> = {
  gerencias: 'gerencias',
  usuarios: 'usuarios',
  clientes: 'clientes',
  locais: 'locais',
  conjuntos: 'conjuntos',
  unidades: 'unidades',
  checklists: 'checklists',
  tiposServicos: 'tiposServicos',
};

const emptyFilterPage = (): FilterPage => ({ options: [], loading: false, error: false, loaded: false });

const getFilterOptions = (response: unknown): filterObj[] => {
  const rawOptions = (response as { items?: unknown })?.items;
  return Array.isArray(rawOptions)
    ? rawOptions.map((option: any) => ({ ...option, id: String(option.id), nome: option.nome ?? '' }))
    : [];
};

export type selectedFilter = {
  dataInicio: Date | number,
  dataFim: Date | number,
  texto?: string | null,
  limite: number,
};

type SelectOption = { label: string, value: string | number };

const toStringOptions = (values: SelectOption[]): { label: string, value: string }[] => {
  return values.map((value) => ({
    label: value.label,
    value: String(value.value)
  }));
};

export default function ReportsTable() {
  const { hierarchyLabel, userData } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [gerenciasNome, setGerenciasNome] = useState<{ label: string, value: string }[] | null>([]);
  const [usuarioNome, setUsuarioNome] = useState<{ label: string, value: string }[] | null>([]);
  // const [equipeNome, setEquipeNome] = useState<{ label: string, value: string }[] | null>([]);
  const [clienteNome, setClienteNome] = useState<{ label: string, value: string }[] | null>([]);
  const [formNome, setFormNome] = useState<{ label: string, value: string }[] | null>([]);
  const [localNome, setLocalNome] = useState<{ label: string, value: string }[] | null>([]);
  const [conjuntoNome, setConjuntoNome] = useState<{ label: string, value: string }[] | null>([]);
  const [unidadeNome, setUnidadeNome] = useState<{ label: string, value: string }[] | null>([]);
  const [servicoNome, setServicoNome] = useState<{ label: string, value: string }[] | null>([]);
  const [identificadorNome, setIdentificadorNome] = useState<{ label: string, value: string }[] | null>([]);
  const [pausa, setPausa] = useState<boolean | null>(null);
  const [filtroPrazo, setFiltroPrazo] = useState('todos');
  const [delegado, setDelegado] = useState<boolean | null>(null);
  const [filteringByScore, setFilteringByScore] = useState(false);
  const [scoreDe, setScoreDe] = useState(0);
  const [scoreAte, setScoreAte] = useState(100);
  const [status, setStatus] = useState<boolean | null>(null);
  const [lastIndexesByPage, setLastIndexesBypage] = useState<{ [page: number]: number } | undefined>();
  const [identificadorOpcoes, setIdentificadorOpcoes] = useState<string[]>([]);
  const [filterPages, setFilterPages] = useState<Partial<Record<FilterEntity, FilterPage>>>({});
  const filterCache = useRef<Partial<Record<FilterEntity, FilterPage>>>({});
  const pendingFilterPages = useRef(new Set<string>());

  const filterBySelectedManagement = (options: filterObj[] = []) => options.filter((option) => {
    if (!gerenciasNome?.length) return true;
    return Boolean(option.gerencias?.some((managementId) =>
      gerenciasNome.some((management) => management.value === managementId)
    ));
  });

  const getFilterPage = useCallback((filter: FilterEntity) => filterPages[filter] ?? filterCache.current[filter] ?? emptyFilterPage(), [filterPages]);

  const loadFilter = useCallback(async (filter: FilterEntity) => {
    const current = filterCache.current[filter] ?? emptyFilterPage();
    if (current.loading || current.loaded) return;
    const requestKey = filter;
    if (pendingFilterPages.current.has(requestKey)) return;

    pendingFilterPages.current.add(requestKey);
    const loadingPage = { ...current, loading: true, error: false };
    filterCache.current[filter] = loadingPage;
    setFilterPages(previous => ({ ...previous, [filter]: loadingPage }));
    try {
      const token = await AttToken();
      if (!token) throw new Error('Token não encontrado');
      const response = await reportsService.getFiltersByEntity(token, entityByFilter[filter]);
      const optionsFromApi = getFilterOptions(response);
      const seen = new Set(current.options.map(option => option.id));
      const options = [...current.options, ...optionsFromApi.filter(option => !seen.has(option.id))];
      const nextPage: FilterPage = {
        options,
        loading: false,
        error: false,
        loaded: true,
      };
      filterCache.current[filter] = nextPage;
      setFilterPages(previous => ({ ...previous, [filter]: nextPage }));
    } catch (error) {
      const failedPage = { ...current, loading: false, error: true };
      filterCache.current[filter] = failedPage;
      setFilterPages(previous => ({ ...previous, [filter]: failedPage }));
    } finally {
      pendingFilterPages.current.delete(requestKey);
    }
  }, []);

  const filterAutocompleteProps = useCallback((filter: FilterEntity) => {
    const page = getFilterPage(filter);
    return {
      loading: page.loading,
      error: page.error,
      loaded: page.loaded,
      onOpen: () => loadFilter(filter),
    };
  }, [getFilterPage, loadFilter]);

  const data: filterDataType = {
    gerencias: getFilterPage('gerencias').options,
    users: getFilterPage('usuarios').options,
    clientes: getFilterPage('clientes').options,
    tags: [],
    forms: getFilterPage('checklists').options,
    locais: getFilterPage('locais').options,
    //equipes: [],
    conjuntos: getFilterPage('conjuntos').options,
    unidades: getFilterPage('unidades').options,
    tiposServicos: getFilterPage('tiposServicos').options,
  };
  const loading = false;

  const [filters, setFilters] = useState<selectedFilter>({
    dataInicio: oneWeekAgo.setDate(currentDate.getDate() - 7),
    dataFim: new Date().setHours(23, 59, 59, 59),
    texto: null,
    limite: 0,
  });
  const [pageNumber, setPageNumber] = useState(0);
  const [itensPerPage, setItensPerPage] = useState(10);
  const [totalItens, setTotalItens] = useState(10);
  const [executions, setExecutions] = useState<ChecklistReportType[]>([]);
  const [searchVersion, setSearchVersion] = useState(0);

  const sendDataForSupervisor = () => {
    return conjuntoNome && conjuntoNome.length ? conjuntoNome?.map((elem) => elem.value) : data?.conjuntos?.map(el => el.id) ?? [];
  };

  const sendDataForManager = () => {
    return gerenciasNome && gerenciasNome.length ? gerenciasNome?.map((elem) => elem.value) : data?.gerencias?.map(el => el.id) ?? [];
  };

  const { data: filteredData, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_REPORTS_DATA', searchVersion, pageNumber, itensPerPage],
    queryFn: async () => {
      toast.info('Buscando relatórios...', { toastId: 'buscando' });
      const token = await AttToken();
      if (token) {
        const newFilters: filtersV2 = {
          inicio: {
            de: Number(filters.dataInicio),
            ate: Number(filters.dataFim)
          },
          score: filteringByScore ? {
            de: scoreDe,
            ate: scoreAte
          } : undefined,
          cliente: clienteNome?.map((elem) => elem.value),
          local: localNome?.map((elem) => elem.value),
          conjunto: userData.role === 'Supervisor' ? sendDataForSupervisor() : conjuntoNome?.map((elem) => elem.value),
          unidade: unidadeNome?.map((elem) => elem.value),
          usuario: userData.role === 'Usuario' ? [userData.id] : usuarioNome?.map((elem) => elem.value),
          // equipe: equipeNome?.map((elem) => elem.value),
          tipoServico: servicoNome?.map((elem) => elem.value),
          gerencia: userData.role === 'Gerente' ? sendDataForManager() : gerenciasNome?.map((elem) => elem.value),
          pausa: filtroPrazo === 'todos' ? (pausa === null ? undefined : pausa) : undefined,
          pausaExpirada: filtroPrazo === 'pausa-expirada' ? true : undefined,
          pausaDentroDoPrazo: filtroPrazo === 'pausa-dentro-prazo' ? true : undefined,
          execucaoExpirada: filtroPrazo === 'execucao-expirada' ? true : undefined,
          delegado: delegado === null ? undefined : delegado,
          status: status === null ? undefined : status,
          formulario: formNome?.map((elem) => elem.value),
          identificador: identificadorNome?.map((elem) => elem.label)
        };
        const response = await reportsService.getExecutionsV2(token, newFilters, lastIndexesByPage && lastIndexesByPage[pageNumber] ? lastIndexesByPage[pageNumber] : null, itensPerPage);
        if (!response) throw new Error('erro ao buscar relatorios');
        // The reports service can return either the payload itself or wrap it in
        // `data`. Always hand the table the actual report payload.
        return response?.data ?? response;
      }
    },
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 60 * 1000
  });

  const onLoading = isLoading || isFetching || loading;

  const handleFiltroPrazo = (value: string) => {
    setFiltroPrazo(value);
    if (value !== 'todos') setPausa(null);
    setLastIndexesBypage(undefined);
    setPageNumber(0);
  };

  const mensagem = "O número total de combinações entre os filtros selecionados excede o limite de 30. Reduza a quantidade de opções para prosseguir.Exemplo: selecionar 3 clientes e 4 formulários gera 12 combinações (3 × 4).";

  useEffect(() => {
    if (isSuccess) {
      if (filteredData?.message === mensagem) {
        toast.info(mensagem, { toastId: 'erro2' });
      }
      if (filteredData?.execucoes?.length === 0) {
        toast.info(t('Reports.reportsTable.noReportsFound'), { toastId: 'zero' });
      }
      if (filteredData?.identificadorOpcoes && filteredData?.identificadorOpcoes?.length) {
        setIdentificadorOpcoes(filteredData?.identificadorOpcoes);
      }
      if (filteredData?.lastIndex && filteredData?.total && filteredData?.total > (itensPerPage * (pageNumber || 1))) {
        setLastIndexesBypage(prev => {
          if (prev) {
            return {
              ...prev,
              [pageNumber + 1]: filteredData?.lastIndex
            };
          } else {
            return { [pageNumber + 1]: filteredData?.lastIndex };
          }
        });
      }
      setTotalItens(filteredData?.total ?? 0);
      setExecutions(filteredData?.execucoes ?? []);
    }
    if (isError) {
      setExecutions([]);
      setTotalItens(0);
      setLastIndexesBypage(undefined);
      setIdentificadorOpcoes([]);
      toast.info(t('Reports.reportsTable.noReportsFound'), { toastId: 'zero' });
      trackEventMatomo('relatórios', 'erro', 'carregamento', 'falha ao carregar relatórios');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, filteredData, isSuccess, isError]);

  return (
    <>
      <Card sx={{ padding: '15px 15px 0 15px', marginBottom: '20px' }}>
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack
            width='100%'
            direction="column"
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
          >
            <Stack
              width='100%'
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              spacing={1}
            >
              <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing={1}
              >
                <span>{t('Reports.searchFilterSettings')}</span>
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DesktopDatePicker
                      disabled={isLoading || loading}
                      maxDate={new Date(new Date().setHours(0, 0, 0))}
                      format='dd/MM/yyyy'
                      label={t('Reports.from')}
                      slotProps={{
                        textField: { size: 'small' },
                        field: {
                          readOnly: true,
                        },
                      }}
                      value={new Date(filters.dataInicio)}
                      onChange={(newValue) => {
                        if (!newValue) return;
                        trackEventMatomo('Relatórios', 'click', 'datepicker', 'seleciona data');
                        const date = new Date(newValue.getTime());
                        date.setHours(0, 0, 0, 0);
                        setFilters({ ...filters, dataInicio: date });
                      }}
                    />
                    <DesktopDatePicker
                      disabled={(isLoading || loading) || filters.dataFim < filters.dataInicio}
                      format='dd/MM/yyyy'
                      label={t('Reports.until')}
                      value={new Date(filters.dataFim)}
                      slotProps={{
                        textField: { size: 'small' },
                        field: {
                          readOnly: true,
                        },
                      }}
                      maxDate={new Date(new Date().setHours(23, 59, 59))}
                      onChange={(newValue) => {
                        if (!newValue) return;
                        trackEventMatomo('Relatórios', 'click', 'datepicker', 'seleciona data');
                        const date = new Date(newValue.getTime());
                        date.setHours(23, 59, 59, 59);
                        setFilters({ ...filters, dataFim: date });
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Stack>
              <Button
                color='info'
                variant='contained'
                endIcon={<FilterListIcon />}
                onClick={() => {
                  trackEventMatomo('Relatórios', 'click', 'button', 'pesquisar');
                  setLastIndexesBypage(undefined);
                  setPageNumber(0);
                  setItensPerPage(10);
                  setSearchVersion(previous => previous + 1);
                }}
                disabled={
                  isLoading || loading || (
                    userData.role === "Gerente"
                    && gerenciasNome?.length
                    && data?.gerencias
                    && data?.gerencias.length > 30
                  ) || (
                    userData.role === "Supervisor"
                    && !conjuntoNome?.length
                    && data?.conjuntos
                    && data?.conjuntos.length > 30
                  ) || filters.dataFim < filters.dataInicio
                }
              >
                {t('filter')}
              </Button>
              {userData.role === "Gerente" && gerenciasNome && gerenciasNome?.length > 0 && data?.gerencias && data?.gerencias.length > 30 && (
                <span
                  className='tag is-warning'
                >
                  {`${t('Reports.reportsTable.youAreInMoreThan30')} ${hierarchyLabel.gerencia} ${t('Reports.reportsTable.pleaseSelectUpTo30')}`}
                </span>
              )}
              {userData.role === "Supervisor" && !conjuntoNome?.length && data?.conjuntos && data?.conjuntos.length > 30 && (
                <span
                  className='tag is-warning'
                >
                  {`${t('Reports.reportsTable.youSuperviseMoreThan30')} ${hierarchyLabel.conjunto} ${t('Reports.reportsTable.pleaseSelectUpTo30')}`}
                </span>
              )}
            </Stack>
            <Accordion style={{ width: '100%', padding: '15px' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <SpanTitle>
                  {t('table.title')}
                </SpanTitle>
              </ AccordionSummary>
              <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={1.5}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <AutocompleteSemApi
                    selectedValues={gerenciasNome ?? []}
                    options={[...new Set(data?.gerencias)]
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []}
                    onChange={(values: SelectOption[]) => setGerenciasNome(toStringOptions(values))}
                    textLabel={`${t('Reports.managementSelect')} - (${hierarchyLabel.gerencia || 'Gerencia'})`}
                    {...filterAutocompleteProps('gerencias')}
                  />
                  <div style={{ width: '100%' }}>
                    <AutocompleteSemApi
                      selectedValues={usuarioNome ?? []}
                      options={[...new Set(data?.users)]
                        ?.filter((el) => {
                          if (!gerenciasNome?.length) return el;
                          if (el.gerencias && gerenciasNome?.length) return el.gerencias
                            ?.some((gerId: string) => gerenciasNome
                              ?.some(ger => ger.value === gerId));
                          else return false;
                        })
                        ?.map((value: filterObj) => (
                          { label: value.nome, value: value.id, }
                        )) ?? []}
                      onChange={(values: SelectOption[]) => setUsuarioNome(toStringOptions(values))}
                      textLabel={t('Reports.multipleUserSelect')}
                      {...filterAutocompleteProps('usuarios')}
                    />
                    {userData.role === 'Usuario' && <WaringSpan>{t('Reports.disabledForUser')}</WaringSpan>}
                  </div>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <AutocompleteSemApi
                    selectedValues={clienteNome ?? []}
                    options={data?.clientes
                      ?.filter((el) => {
                        if (!gerenciasNome?.length) return el;
                        if (el.gerencias && gerenciasNome?.length) return el.gerencias.some(gerId => gerenciasNome.some(ger => ger.value === gerId));
                        else return false;
                      })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []
                    }
                    onChange={(val: SelectOption[]) => setClienteNome(toStringOptions(val))}
                    textLabel={`${t('Reports.multipleCustomerSelect')} - (${hierarchyLabel.cliente || 'Cliente'})`}
                    {...filterAutocompleteProps('clientes')}
                  />
                  <AutocompleteSemApi
                    selectedValues={localNome ?? []}
                    options={data?.locais
                      ?.filter((el) => {
                        if (!gerenciasNome?.length) return el;
                        if (el.gerencias && gerenciasNome?.length) return el.gerencias.some(gerId => gerenciasNome.some(ger => ger.value === gerId));
                        else return false;
                      })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []}
                    onChange={(val: SelectOption[]) => setLocalNome(toStringOptions(val))}
                    textLabel={`${t('Reports.multiplePlaceSelect')} - (${hierarchyLabel.local || 'Local'})`}
                    {...filterAutocompleteProps('locais')}
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <AutocompleteSemApi
                    selectedValues={conjuntoNome ?? []}
                    options={data?.conjuntos
                      ?.filter((el) => {
                        if (!gerenciasNome?.length) return el;
                        if (el.gerencias && gerenciasNome?.length) return el.gerencias.some(gerId => gerenciasNome.some(ger => ger.value === gerId));
                        else return false;
                      })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []
                    }
                    onChange={(val: SelectOption[]) => setConjuntoNome(toStringOptions(val))}
                    textLabel={`${t('Reports.multipleSetSelect')} - (${hierarchyLabel.conjunto || 'Conjunto'})`}
                    {...filterAutocompleteProps('conjuntos')}
                  />
                  <AutocompleteSemApi
                    selectedValues={unidadeNome ?? []}
                    options={data?.unidades
                      ?.filter((el) => {
                        if (!gerenciasNome?.length) return el;
                        if (el.gerencias && gerenciasNome?.length) return el.gerencias.some(gerId => gerenciasNome.some(ger => ger.value === gerId));
                        else return false;
                      })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []
                    }
                    onChange={(val: SelectOption[]) => setUnidadeNome(toStringOptions(val))}
                    textLabel={`${t('Reports.multipleUnitySelect')} - (${hierarchyLabel.unidade || 'Unidade'})`}
                    {...filterAutocompleteProps('unidades')}
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1}
                >
                  <AutocompleteSemApi
                    selectedValues={formNome ?? []}
                    options={data?.forms
                      ?.filter((el) => {
                        if (!gerenciasNome?.length) return el;
                        if (el.gerencias && gerenciasNome?.length) return el.gerencias.some(gerId => gerenciasNome.some(ger => ger.value === gerId));
                        else return false;
                      })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []
                    }
                    onChange={(val: SelectOption[]) => setFormNome(toStringOptions(val))}
                    textLabel={`${t('Reports.selectSpecificForms')}`}
                    {...filterAutocompleteProps('checklists')}
                  />
                  <AutocompleteSemApi
                    selectedValues={servicoNome ?? []}
                    options={filterBySelectedManagement(data?.tiposServicos)
                      // ?.filter((elem) => !servicoNome?.some(service => service?.value === elem?.id))
                      ?.sort((a, b) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1; })
                      ?.map((value: filterObj) => (
                        { label: value.nome, value: value.id, }
                      )) ?? []
                    }
                    onChange={(val: SelectOption[]) => setServicoNome(toStringOptions(val))}
                    textLabel={t('Reports.multipleServiceSelect')}
                    {...filterAutocompleteProps('tiposServicos')}
                  />
                </Stack>
                <Autocomplete
                  autoComplete
                  onClick={() => trackEventMatomo('Relatórios', 'click', 'select', 'seleciona valor')}
                  multiple
                  getOptionLabel={option => option.label as string}
                  getOptionKey={option => option.value}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  id="combo-box-demo"
                  filterSelectedOptions
                  disabled={!identificadorOpcoes?.length || isLoading || loading}
                  noOptionsText={t('Reports.reportsTable.noIdentifiersFound')}
                  value={identificadorNome as { value: string, label: string }[]}
                  options={identificadorOpcoes
                    ?.filter((elem) => !identificadorNome?.some(identifica => identifica?.label === elem))
                    ?.sort((a, b) => { return a?.toLowerCase() > b?.toLowerCase() ? 1 : -1; })
                    ?.map((option) => ({
                      label: option,
                      value: option,
                    }))}
                  onChange={(_event, newValue: { label: string, value: string }[] | null) => {
                    setIdentificadorNome(newValue);
                  }}
                  renderInput={(params) =>
                    <TextField {...params} variant="outlined"
                      label={!identificadorOpcoes?.length
                        ? `${t('Reports.makeFirstQueryToFilterByIdentifier')}`
                        : t('Reports.multipleIdentifierSelect')} />}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Stack width='100%' spacing={2}>
                    <FormControl fullWidth variant='outlined' disabled={isLoading || loading}>
                    <InputLabel id="demo-simple-select-label">{t('Reports.reportsTable.byPause')}</InputLabel>
                    <Select
                      onClick={() => trackEventMatomo('Relatórios', 'click', 'select', 'seleciona valor')}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={pausa === null ? 'both' : pausa ? 'paused' : 'finished'}
                      label={t('Reports.reportsTable.byPause')}
                      onChange={(e) => {
                        const value = e.target.value;
                        switch (value) {
                          case 'both':
                            setPausa(null);
                            break;

                          case 'paused':
                            setPausa(true);
                            break;

                          case 'finished':
                            setPausa(false);
                            break;

                          default:
                            setPausa(null);
                            break;
                        }
                      }}
                    >
                      <MenuItem value="both">{t('Reports.reportsTable.pausedAndFinishedForms') as string}</MenuItem>
                      <MenuItem value="paused">{t('Reports.reportsTable.onlyPausedForms') as string}</MenuItem>
                      <MenuItem value="finished">{t('Reports.reportsTable.onlyFinishedForms') as string}</MenuItem>
                    </Select>
                  </FormControl>
                    <FormControl fullWidth variant='outlined' disabled={isLoading || loading}>
                      <InputLabel id="deadline-status-select-label">Estado do prazo</InputLabel>
                      <Select
                        onClick={() => trackEventMatomo('Relatórios', 'click', 'select', 'seleciona estado do prazo')}
                        labelId="deadline-status-select-label"
                        id="deadline-status-select"
                        value={filtroPrazo}
                        label="Estado do prazo"
                        onChange={(e) => handleFiltroPrazo(e.target.value)}
                      >
                        <MenuItem value="todos">Todos</MenuItem>
                        <MenuItem value="pausa-dentro-prazo">Pausadas dentro do prazo</MenuItem>
                        <MenuItem value="pausa-expirada">Pausadas expiradas</MenuItem>
                        <MenuItem value="execucao-expirada">Execuções expiradas</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack width='100%' spacing={2}>
                    <FormControl fullWidth variant='outlined' disabled={isLoading || loading}>
                    <InputLabel id="demo-simple-select-label">{t('Reports.reportsTable.byStatus')}</InputLabel>
                    <Select
                      onClick={() => trackEventMatomo('Relatórios', 'click', 'select', 'seleciona valor')}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={status === null ? 'both' : status ? 'active' : 'canceled'}
                      label={t('Reports.reportsTable.byStatus')}
                      onChange={(e) => {
                        const value = e.target.value;
                        switch (value) {
                          case 'both':
                            setStatus(null);
                            break;

                          case 'active':
                            setStatus(true);
                            break;

                          case 'canceled':
                            setStatus(false);
                            break;

                          default:
                            setStatus(null);
                            break;
                        }
                      }}
                    >
                      <MenuItem value="both">{t('Reports.reportsTable.activeAndCanceledForms') as string}</MenuItem>
                      <MenuItem value="active">{t('Reports.reportsTable.onlyActiveForms') as string}</MenuItem>
                      <MenuItem value="canceled">{t('Reports.reportsTable.onlyCanceledForms') as string}</MenuItem>
                    </Select>
                  </FormControl>
                    <FormControl fullWidth variant='outlined' disabled={isLoading || loading}>
                    <InputLabel id="demo-simple-select-label">{t('Reports.reportsTable.byDelegation')}</InputLabel>
                    <Select
                      onClick={() => trackEventMatomo('Relatórios', 'click', 'select', 'seleciona valor')}
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={delegado === null ? 'both' : delegado ? 'delegado' : 'naoDelegado'}
                      label="Por Delegação"
                      onChange={(e) => {
                        const value = e.target.value;
                        switch (value) {
                          case 'both':
                            setDelegado(null);
                            break;

                          case 'delegado':
                            setDelegado(true);
                            break;

                          case 'naoDelegado':
                            setDelegado(false);
                            break;

                          default:
                            setDelegado(null);
                            break;
                        }
                      }}
                    >
                      <MenuItem value="both">{t('Reports.reportsTable.formsDelegatedAndNotDelegated') as string}</MenuItem>
                      <MenuItem value="delegado">{t('Reports.reportsTable.onlyDelegatedForms') as string}</MenuItem>
                      <MenuItem value="naoDelegado">{t('Reports.reportsTable.onlyNotDelegatedForms') as string}</MenuItem>
                    </Select>
                  </FormControl>
                  </Stack>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  <LabelInput className='label checkbox'>
                    <input
                      disabled={isLoading || loading}
                      onClick={() => trackEventMatomo('Relatórios', 'click', 'input', 'edita valor')}
                      checked={filteringByScore}
                      type='checkbox'
                      onChange={(e) => setFilteringByScore(e.target.checked)}
                      style={{ marginRight: '5px' }}
                    />
                    {t('Reports.filterByScoreRange')}
                  </LabelInput>
                  <TextField
                    label={t('Reports.reportsTable.from')}
                    onClick={() => trackEventMatomo('Relatórios', 'click', 'input', 'edita valor')}
                    onChange={(e) => {
                      if (!isNaN(Number(e.target.value)) && Number(e.target.value) >= 0 && Number(e.target.value) <= 100) {
                        setScoreDe(Number(e.target.value));
                      }
                    }}
                    disabled={!filteringByScore || isLoading || loading}
                    value={scoreDe}
                  />
                  <TextField
                    label={t('Reports.reportsTable.upTo')}
                    onClick={() => trackEventMatomo('Relatórios', 'click', 'input', 'edita valor')}
                    onChange={(e) => {
                      if (!isNaN(Number(e.target.value)) && Number(e.target.value) >= 0 && Number(e.target.value) <= 100) {
                        setScoreAte(Number(e.target.value));
                      }
                    }}
                    disabled={!filteringByScore || isLoading || loading}
                    value={scoreAte}
                  />
                </Stack>
              </Stack>
            </ Accordion>
          </Stack>
        </form>
      </Card>
      {onLoading
        ? <Stack spacing={1} width='100%'>
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} variant='rounded' height={56} width='100%' />
          ))}
        </Stack>
        : <ReportsTableComponent
          currentItems={executions ?? []}
          setItensPerPage={setItensPerPage}
          setPageNumber={setPageNumber}
          pageNumber={pageNumber}
          itensPerPage={itensPerPage}
          limit={totalItens}
        />}
    </>
  );
}
